import { type FC, Suspense, useState, useEffect } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link';
import { HeartPill } from 'dibs-nft-heart-pill/exports/HeartPill';
import { NftHeartPillProvider } from 'dibs-nft-heart-pill/exports/NftHeartPillProvider';
import slugify from 'dibs-slugify/exports/slugify';
import { NftPdpLiveDetailsRefetchWrapper } from '../NftPdpLiveDetails/NftPdpLiveDetailsRefetchWrapper';
import { type NftPdpSidebar_nftItem$key } from './__generated__/NftPdpSidebar_nftItem.graphql';
import { type NftPdpSidebar_viewer$key } from './__generated__/NftPdpSidebar_viewer.graphql';
import styles from './NftPdpSidebar.scss';
import layoutStyles from '../NftPdpRespLayout.scss';

type Props = {
    nftItem: NftPdpSidebar_nftItem$key;
    viewer: NftPdpSidebar_viewer$key;
};

export const NftPdpSidebar: FC<Props> = ({ nftItem: nftItemRef, viewer: viewerRef }) => {
    const [isClient, setIsClient] = useState(false);
    useEffect(() => {
        setIsClient(true);
    }, []);
    const viewer = useFragment(
        graphql`
            fragment NftPdpSidebar_viewer on Viewer {
                ...HeartPill_viewer
                ...NftHeartPillProvider_viewer
                    @arguments(nftIds: [$nftId], userId: $userId, fetchUser: $fetchUser)
                ...NftPdpLiveDetailsRefetchWrapper_viewer
            }
        `,
        viewerRef
    );
    const nftItem = useFragment(
        graphql`
            fragment NftPdpSidebar_nftItem on NftItem {
                serviceId
                creatorAddress {
                    publicProfileUrl
                    publicProfile {
                        profileName
                    }
                }
                metadata {
                    ... on NftDigitalArtMetadata {
                        name
                        createdYear
                    }
                }
                exhibitions {
                    urlLabel
                    heroBanner {
                        bannerConfig {
                            ... on NftCmsExhibitionHtmlBannerType {
                                eyeBrow
                            }
                        }
                    }
                }
                ...HeartPill_nftItem
            }
        `,
        nftItemRef
    );
    const { metadata, creatorAddress } = nftItem || {};
    const { createdYear, name } = metadata || {};
    const displayName = creatorAddress?.publicProfile?.profileName;
    const profileUrl = creatorAddress?.publicProfileUrl || '';
    const nftExhibition = nftItem.exhibitions?.[0];

    return (
        <div className={layoutStyles.sidebar}>
            <div className={styles.itemDetailText}>
                {displayName && <Link href={profileUrl}>{displayName}</Link>}
                {createdYear && `${displayName && createdYear ? ', ' : ''}${createdYear}`}
            </div>
            <h1 className={styles.itemTitle}>
                {name}
                {isClient && !!nftItem.serviceId && (
                    <Suspense fallback={''}>
                        <NftHeartPillProvider viewer={viewer} nftItemIds={[nftItem.serviceId]}>
                            <HeartPill
                                nftItem={nftItem}
                                viewer={viewer}
                                visible
                                containerClass={styles.heartPill}
                                isVertical
                                shouldFillHeartOnHover
                                gaLabel="PDP"
                            />
                        </NftHeartPillProvider>
                    </Suspense>
                )}
            </h1>
            <div className={styles.itemDetailText}>
                {nftExhibition?.urlLabel && (
                    <FormattedMessage
                        id="nft.pdp.sidebar.featuredIn"
                        defaultMessage="Featured In {link}"
                        values={{
                            link: (
                                <Link
                                    href={`/nft-collections/exhibitions/${slugify(
                                        nftExhibition.urlLabel
                                    )}`}
                                >
                                    {nftExhibition?.heroBanner?.[0]?.bannerConfig?.eyeBrow || (
                                        <FormattedMessage
                                            id="nft.pdp.sidebar.featuredInFallback"
                                            defaultMessage="Exhibition"
                                        />
                                    )}
                                </Link>
                            ),
                        }}
                    />
                )}
            </div>
            {isClient && (
                <Suspense fallback={''}>
                    <NftPdpLiveDetailsRefetchWrapper viewer={viewer} />
                </Suspense>
            )}
        </div>
    );
};
